import { useState, FormEvent, useEffect } from 'react';

const FORM_ACTION_URL = 'https://readdy.ai/api/form/d71a9c0hb0rqicn68dn0';

export default function ContactFormSection() {
  const [formData, setFormData] = useState({
    contactName: '',
    phoneNumber: '',
    dealerName: '',
    mainChallenge: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // 倒數計時：每月重置，計算到本月底剩餘時間
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calcTimeLeft = () => {
      const now = new Date();
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0);
      const diff = endOfMonth.getTime() - now.getTime();
      if (diff <= 0) return;
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    calcTimeLeft();
    const timer = setInterval(calcTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (formData.mainChallenge.length > 500) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('contactName', formData.contactName);
      formBody.append('phoneNumber', formData.phoneNumber);
      formBody.append('dealerName', formData.dealerName);
      formBody.append('mainChallenge', formData.mainChallenge);

      const response = await fetch(FORM_ACTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ contactName: '', phoneNumber: '', dealerName: '', mainChallenge: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-form" className="py-20 md:py-28 bg-[#0A192F] relative overflow-hidden">
      {/* 背景裝飾 */}
      <div className="absolute inset-0 opacity-25 pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-[#00D4FF] rounded-full blur-[120px]"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#D4AF37] rounded-full blur-[120px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* ── 緊迫感橫幅 ── */}
        <div className="max-w-3xl mx-auto mb-10">
          {/* 限量名額進度條 */}
          <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                </span>
                <span className="text-white font-bold text-sm tracking-wide">本月名額即將額滿</span>
              </div>
              <span className="text-red-400 font-extrabold text-sm">剩餘 3 / 10 名</span>
            </div>
            {/* 進度條 */}
            <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-red-500 to-orange-400 transition-all duration-700"
                style={{ width: '70%' }}
              ></div>
            </div>
            <p className="text-gray-500 text-xs mt-2">已有 7 家企業完成本月名額預約</p>
          </div>

          {/* 倒數計時器 */}
          <div className="bg-gradient-to-r from-[#0D1A2A] to-[#0A1628] border border-[#D4AF37]/25 rounded-2xl px-6 py-5">
            <p className="text-center text-[#D4AF37] text-xs font-semibold tracking-widest uppercase mb-4">
              本月優惠方案截止倒數
            </p>
            <div className="flex items-center justify-center gap-3 sm:gap-5">
              {[
                { value: timeLeft.days, label: '天' },
                { value: timeLeft.hours, label: '時' },
                { value: timeLeft.minutes, label: '分' },
                { value: timeLeft.seconds, label: '秒' },
              ].map((unit, i) => (
                <>
                  <div key={`unit-${i}`} className="flex flex-col items-center">
                    <div className="bg-[#060F1E] border border-[#D4AF37]/30 rounded-xl w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center">
                      <span className="text-2xl sm:text-3xl font-extrabold text-white tabular-nums">
                        {String(unit.value).padStart(2, '0')}
                      </span>
                    </div>
                    <span className="text-gray-500 text-xs mt-1.5">{unit.label}</span>
                  </div>
                  {i < 3 && (
                    <span key={`sep-${i}`} className="text-[#D4AF37] text-2xl font-bold mb-4 select-none">:</span>
                  )}
                </>
              ))}
            </div>
            <p className="text-center text-gray-500 text-xs mt-4">
              逾期後本月方案將關閉受理，下月重新開放
            </p>
          </div>
        </div>

        {/* Section Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] px-5 py-2 rounded-full text-xs sm:text-sm font-semibold mb-6">
            <i className="ri-rocket-line"></i>
            <span>啟動引擎 · 免費諮詢</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            預約你的數位轉型診斷
          </h2>
          <p className="text-gray-400 text-sm sm:text-base mb-2">
            Book Your Transformation Diagnosis
          </p>
          <div className="inline-block bg-[#D4AF37]/15 border border-[#D4AF37]/30 rounded-full px-4 sm:px-6 py-2 mt-2">
            <p className="text-[#D4AF37] text-xs sm:text-sm font-semibold">
              ⚡ 每月限額開放 10 家企業 · 顧問 24H 內回覆
            </p>
          </div>
        </div>

        {/* Form Card */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-10">
            <div className="flex items-center justify-center gap-3 mb-6 md:mb-8">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-shield-star-line text-2xl text-[#D4AF37]"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white">
                數位引擎啟動申請表
              </h3>
            </div>

            <form id="engine-start-form" data-readdy-form onSubmit={handleSubmit}>
              <div className="space-y-5">
                {/* 姓名 */}
                <div>
                  <label className="block text-white/80 font-medium mb-2 text-sm">
                    姓名 <span className="text-[#D4AF37]">*</span>
                  </label>
                  <input
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all text-sm"
                    placeholder="您的姓名"
                    required
                  />
                </div>

                {/* 手機 */}
                <div>
                  <label className="block text-white/80 font-medium mb-2 text-sm">
                    手機 <span className="text-[#D4AF37]">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all text-sm"
                    placeholder="0912-345-678"
                    required
                  />
                </div>

                {/* 企業名稱 */}
                <div>
                  <label className="block text-white/80 font-medium mb-2 text-sm">
                    企業 / 品牌名稱 <span className="text-[#D4AF37]">*</span>
                  </label>
                  <input
                    type="text"
                    name="dealerName"
                    value={formData.dealerName}
                    onChange={(e) => setFormData({ ...formData, dealerName: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all text-sm"
                    placeholder="例：台北精選 O2O 展間 / 品牌企業"
                    required
                  />
                </div>

                {/* 您目前的挑戰 */}
                <div>
                  <label className="block text-white/80 font-medium mb-2 text-sm">
                    您目前的挑戰 <span className="text-[#D4AF37]">*</span>
                  </label>
                  <textarea
                    name="mainChallenge"
                    value={formData.mainChallenge}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, mainChallenge: e.target.value });
                      }
                    }}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all resize-none text-sm"
                    rows={5}
                    placeholder="例如：業績難以追蹤、廣告費沒效果、名單管理混亂、無法有效追蹤客源…請描述您目前最大的痛點。"
                    maxLength={500}
                    required
                  />
                  <div className="text-right text-xs text-white/40 mt-1">
                    {formData.mainChallenge.length}/500
                  </div>
                </div>
              </div>

              {/* 提交狀態 */}
              {submitStatus === 'success' && (
                <div className="mt-6 p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg">
                  <p className="text-[#D4AF37] text-center font-medium text-sm">
                    ✓ 申請已成功送出！顧問將於 24 小時內與您聯繫。
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-center font-medium text-sm">
                    ✗ 送出失敗，請稍後再試或直接聯繫我們。
                  </p>
                </div>
              )}

              {/* 提交按鈕 */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-6 md:mt-8 bg-gradient-to-r from-[#D4AF37] to-[#F0C040] text-[#060F1E] py-4 rounded-lg text-base font-bold hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '送出中...' : '🚀 預約我的免費系統展示'}
              </button>

              <p className="text-white/40 text-xs text-center mt-4">
                🔒 您的資料將受到嚴格保密，僅用於診斷評估與顧問聯繫
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
